<script>import * as d3 from "d3";
import SVGRoot from "./SVGRoot.svelte";
import TimeAxis from "./time-axis//TimeAxis.svelte";
import { getTextMetrics } from "../util/text-size.js";
import { afterUpdate, setContext } from "svelte";
import { writable } from "svelte/store";
import {
  timeRangeContextKey
} from "./time-plot-context.js";
import {
  locale,
  timeZone,
  timeRange
} from "../reports/global-store.js";
export let showAxis = void 0;
export let showHistogram = void 0;
export let separate = true;
export let timeEther = true;
export let role = "timeline";
export let startMarker = void 0;
export let endMarker = void 0;
const timeAxisHeight = 64;
const timeAxisMarginRight = 100;
const scalarTickFont = "14px/22px Roboto, sans-serif";
let plotParamMap = /* @__PURE__ */ new Map();
const setPlot = (params) => {
  const isUpdate = plotParamMap.get(params.id);
  plotParamMap.set(params.id, params);
  if (isUpdate) {
    plotParamMap = plotParamMap;
  }
};
let context = {
  timeAxisDefinition: writable(void 0),
  timeScale: writable(void 0),
  range: timeRange,
  tickTextMetrics: getTextMetrics("gX", scalarTickFont),
  setPlot,
  plotConfigMap: writable(/* @__PURE__ */ new Map())
};
setContext(timeRangeContextKey, context);
const { timeAxisDefinition, timeScale, range, plotConfigMap } = context;
let plotIds = [];
let maxLeftIndent = 0;
let maxRightIndent = 0;
let width = 512;
let height = timeAxisHeight;
let gPlots;
afterUpdate(() => {
  plotIds = Array.from(gPlots.children).map(
    (gPlot) => gPlot.dataset.plot
  );
});
$: {
  const plotParams = plotIds.map(
    (plotId) => plotParamMap.get(plotId)
  );
  const plotConfigs = plotParams.map((params) => {
    const axis = separate ? showAxis === void 0 ? params.axis : showAxis : false;
    const histogram = separate ? showHistogram === void 0 ? params.histogram : showAxis : false;
    return {
      id: params.id,
      axis,
      histogram,
      transform: "",
      leftIndent: axis ? params.margin[3] : 0,
      rightIndent: histogram ? params.margin[1] : 0
    };
  });
  maxLeftIndent = plotConfigs.map((config) => config.leftIndent).reduce((max, margin) => Math.max(max, margin), 0);
  maxRightIndent = plotConfigs.map((config) => config.rightIndent).reduce((max, margin) => Math.max(max, margin), 0);
  let transformY = 0;
  plotConfigs.forEach((config, i) => {
    config.leftIndent = maxLeftIndent;
    config.rightIndent = maxRightIndent;
    config.transform = `translate(0,${transformY})`;
    if (separate) {
      transformY += plotParams[i].height;
    }
  });
  $plotConfigMap = plotConfigs.reduce((map, config) => {
    map.set(config.id, config);
    return map;
  }, /* @__PURE__ */ new Map());
}
;
$:
  timelineDataHeight = plotIds.map((plotId) => plotParamMap.get(plotId).height).reduce(
    (sum, height2) => separate ? sum + height2 : Math.max(sum, height2),
    0
  );
$:
  timelineEtherLength = timelineDataHeight - (role === "plot" ? plotIds.length > 0 ? plotParamMap.get(plotIds[plotIds.length - 1]).margin[0] : 0 : 0);
let timeAxisScale;
$: {
  const rangeWidth = width - maxRightIndent - maxLeftIndent;
  const domainWidth = $range.end.getTime() - $range.end.getTime();
  const mspp = domainWidth / rangeWidth;
  const maxRightDomainWidth = mspp * maxRightIndent;
  const maxLeftDomainWidth = mspp * maxLeftIndent;
  $timeScale = d3.scaleTime(
    [$range.start.getTime(), $range.end.getTime()],
    [
      maxLeftIndent,
      width - Math.max(role === "plot" ? timeAxisMarginRight : 0, maxRightIndent)
    ]
  );
  timeAxisScale = role === "plot" ? $timeScale : d3.scaleTime(
    [
      $range.start.getTime() - maxLeftDomainWidth,
      $range.end.getTime() + maxRightDomainWidth
    ],
    [0, width]
  );
}
$: {
  height = timeAxisHeight + timelineDataHeight;
}
$: {
  startMarker = $timeAxisDefinition ? {
    millis: $range.start.getTime(),
    label: $timeAxisDefinition.formatters.bound($range.start)
  } : void 0;
  endMarker = $timeAxisDefinition ? {
    millis: $range.end.getTime(),
    label: $timeAxisDefinition.formatters.bound($range.end)
  } : void 0;
}
let rootSize;
let viewBox = [width, height];
$: {
  if (rootSize) {
    width = rootSize.width;
    viewBox = [width, height];
  }
}
</script>

<SVGRoot bind:bounds={rootSize} {viewBox} tag={role}>
  <g transform="translate(0,{timelineDataHeight})"
    ><TimeAxis
      scale={timeAxisScale}
      start={$range.start.getTime()}
      end={$range.end.getTime()}
      ether={timeEther ? -timelineEtherLength : timelineEtherLength}
      timeZone={$timeZone}
      locale={$locale}
      {role}
      bind:definition={$timeAxisDefinition}
    /></g
  >
  <g bind:this={gPlots} class="plots">
    <slot />
  </g>
</SVGRoot>

<style>
  :global(svg.timeline) {
    background-color: hsla(228, 9%, 11%, 0.6);
    color: white;
  }

  :global(svg g.axis line.ether) {
    stroke-opacity: 0.5;
  }

  :global(svg g.axis),
  :global(svg g.axis.scalar-axis g.tick) {
    font:
      14px/22px Roboto,
      sans-serif;
  }
</style>
